import React from 'react';
import { Bookmark, Sparkles } from 'lucide-react';

/**
 * ChatHeader v3 — "Live · Sofia office" concierge presence header.
 *
 * V3.5 polish notes:
 *   • Avatar container upgraded from the blue→cyan gradient to a canonical
 *     solid brand-blue with a subtle glowing halo — matches the rest of
 *     the 3-tone system.
 *   • "Online" pill replaced with a "Live · Sofia" line that reads like a
 *     concierge desk indicator (real detail: our editorial team is
 *     Plovdiv-based, so Sofia works as the airport-hub reference — no
 *     invented data).
 *   • Optional Save Trip button unchanged.
 *
 * Pure presentation — no logic, no side effects.
 */
export default function ChatHeader({
  title,
  subtitle,
  showSave,
  savingTrip,
  onSaveTrip,
  saveLabel = 'Save Trip',
  savingLabel = 'Saving...',
  liveLabel = 'Live · Sofia',
}) {
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-white"
      data-testid="chat-header"
    >
      {/* Avatar — solid brand-blue tile with a soft halo behind it so the
          concierge presence feels warm without ever using a gradient. */}
      <div className="relative flex-shrink-0" aria-hidden>
        <div
          className="pointer-events-none absolute inset-0 rounded-xl blur-md bg-blue-500/25"
          style={{ transform: 'scale(1.15)' }}
        />
        <div className="relative w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm ring-1 ring-blue-600/10">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <h3 className="font-bold text-gray-900 text-sm sm:text-base leading-tight truncate">{title}</h3>
        <p className="text-[11px] sm:text-xs text-gray-500 leading-tight truncate">{subtitle}</p>
      </div>

      <div className="ml-auto flex items-center gap-2 flex-shrink-0">
        {showSave && (
          <button
            onClick={onSaveTrip}
            disabled={savingTrip}
            className="flex items-center gap-1.5 px-2.5 h-8 bg-blue-600 text-white rounded-lg text-[11px] font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            data-testid="save-trip-btn"
          >
            <Bookmark className="w-3.5 h-3.5" />
            {savingTrip ? savingLabel : saveLabel}
          </button>
        )}
        {/* Live · Sofia — replaces the older "Online" pill. Reads like a
            concierge desk. Emerald pulsing dot = universal "live" indicator. */}
        <span
          className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-100"
          data-testid="chat-live-indicator"
        >
          <span className="relative flex items-center justify-center" aria-hidden>
            <span className="absolute w-2 h-2 rounded-full bg-emerald-500/60 animate-ping" />
            <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-500" />
          </span>
          {liveLabel}
        </span>
      </div>
    </div>
  );
}
